/**
 * LogEntry.js
 *
 * The SINGLE component that renders ANY entry in the unified voyage log.
 * Switches display based on entry.type.
 *
 * This is the core of the V2 unified log - ONE component renders everything.
 */

import React from 'react';
import {
  MapPin,
  CloudSun,
  Radio,
  FileText,
  Anchor,
  Check,
  X,
  Users,
  Package,
  Ship,
  MessageSquare,
  Fuel,
  ClipboardCheck
} from 'lucide-react';
import { OPERATION_TYPE_LABELS } from '../../../../../config/operationsConfig';
import AttributionBadge from '../../../shared/AttributionBadge';
// FT-161 Phase 3: Weather report card
import { WeatherReportCard } from '../VoyageLogCards';
// Pass 2c §1.2/§1.3 — plan-item-label join for position + operation_complete
// header renames. ID-based lookup against voyage.voyagePlanSnapshot.items.
import { findLabelByPlanItemId } from '../../../../../utils/voyagePlanLogJoin';

/**
 * Format ISO timestamp to HH:MM
 */
const formatTime = (timestamp) => {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

/**
 * Format coordinates for display
 */
const formatCoordinate = (value, isLat) => {
  if (value === null || value === undefined) return '---';
  const abs = Math.abs(value);
  const dir = isLat ? (value >= 0 ? 'N' : 'S') : (value >= 0 ? 'E' : 'W');
  return `${abs.toFixed(4)}°${dir}`;
};

const LogEntry = ({
  entry,
  voyage,  // Pass 2c — threaded through for findLabelByPlanItemId snapshot lookup
  onCheckComplete,
  onCheckSkip,
  onAnnotate,  // Handler for adding annotation
  isEditable = true,
  canModify = true,
  currentPhase = null,  // For phase-aware check display
  isLast = false  // Hide connector line on final entry
}) => {
  const time = formatTime(entry.timestamp);

  // ═══════════════════════════════════════════════════════════════
  // SECTION HEADER (Operation start divider)
  // ═══════════════════════════════════════════════════════════════
  if (entry.type === 'section') {
    const label = OPERATION_TYPE_LABELS[entry.operationType] || entry.operationType;
    return (
      <div className="flex items-center gap-2 py-3 px-4 mt-4 bg-slate-100 border-y border-slate-200">
        <Users className="h-4 w-4 text-slate-500" />
        <span className="text-xs font-bold text-slate-600 tracking-wider uppercase">
          {label}
        </span>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // OPERATION DATA (Platform, Persons, etc.)
  // ═══════════════════════════════════════════════════════════════
  if (entry.type === 'operation_data') {
    return (
      <div className="py-2 px-4 pl-16 bg-slate-50 border-b border-slate-100">
        <div className="text-sm space-y-1">
          {entry.transfer_type && (
            <div className="flex gap-2">
              <span className="text-slate-500 w-24">Type:</span>
              <span className="text-slate-700">{entry.transfer_type}</span>
            </div>
          )}
          {entry.platform && (
            <div className="flex gap-2">
              <span className="text-slate-500 w-24">Platform:</span>
              <span className="text-slate-700">{entry.platform}</span>
            </div>
          )}
          {entry.persons !== undefined && entry.persons !== null && (
            <div className="flex gap-2">
              <span className="text-slate-500 w-24">Persons:</span>
              <span className="text-slate-700">{entry.persons}</span>
            </div>
          )}
          {entry.persons_count !== undefined && entry.persons_count !== null && (
            <div className="flex gap-2">
              <span className="text-slate-500 w-24">Persons:</span>
              <span className="text-slate-700">{entry.persons_count}</span>
            </div>
          )}
          {entry.tow_vessel && (
            <div className="flex gap-2">
              <span className="text-slate-500 w-24">Tow Vessel:</span>
              <span className="text-slate-700">{entry.tow_vessel}</span>
            </div>
          )}
          {entry.tow_name && (
            <div className="flex gap-2">
              <span className="text-slate-500 w-24">Tow:</span>
              <span className="text-slate-700">{entry.tow_name}</span>
            </div>
          )}
          {entry.vessel_name && (
            <div className="flex gap-2">
              <span className="text-slate-500 w-24">Vessel:</span>
              <span className="text-slate-700">{entry.vessel_name}</span>
            </div>
          )}
          {entry.pilot_name && (
            <div className="flex gap-2">
              <span className="text-slate-500 w-24">Pilot:</span>
              <span className="text-slate-700">{entry.pilot_name}</span>
            </div>
          )}
          {entry.direction && (
            <div className="flex gap-2">
              <span className="text-slate-500 w-24">Direction:</span>
              <span className="text-slate-700">{entry.direction}</span>
            </div>
          )}
          {entry.locked && (
            <div className="text-xs text-slate-400 italic mt-1">
              (Data locked - operation in progress)
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // MILESTONE (LET GO, ALL FAST) - Full-width colored cards
  // ═══════════════════════════════════════════════════════════════
  if (entry.type === 'milestone') {
    const isLetGo = entry.milestoneType === 'let_go';
    const label = isLetGo ? 'LET GO' : 'ALL FAST';
    const bgColor = isLetGo ? 'bg-cyan-600' : 'bg-green-600';

    return (
      <div className={`${bgColor} print:bg-white py-4 px-6 my-2`}>
        <div className="flex items-center gap-4">
          {/* Timeline dot - larger for milestones */}
          <div className={`relative flex-shrink-0`}>
            <div className="w-4 h-4 bg-white rounded-full"></div>
            {/* Connector line above (unless first entry) */}
            {!isLast && (
              <div className="absolute left-1/2 top-full -translate-x-1/2 w-0.5 h-4 bg-white/30"></div>
            )}
          </div>

          <Anchor className="h-6 w-6 text-white flex-shrink-0" />
          <span className="text-sm text-white/80 font-mono">{time}</span>
          <span className="text-lg font-bold text-white">
            {label}
            {entry.location && ` - ${entry.location}`}
          </span>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // OPERATION COMPLETE
  // ═══════════════════════════════════════════════════════════════
  if (entry.type === 'operation_complete') {
    // Pass 2c §1.3 — operation_complete plan-item-label join.
    // Replaces the generic "OPERATION COMPLETE" header with the named plan item
    // (e.g. "Pre-Arrival", "Bunkering") when the entry's planItemId resolves.
    const planLabel = findLabelByPlanItemId(voyage, entry.planItemId);
    return (
      <div className="flex items-start gap-3 py-2 px-4 bg-green-50 border-l-4 border-green-500 my-2">
        {/* Timeline dot column */}
        <div className="relative flex-shrink-0 pt-1">
          <div className="w-4 h-4 bg-green-600 rounded-full"></div>
          {/* Vertical connector line */}
          {!isLast && (
            <div className="absolute left-1/2 top-full -translate-x-1/2 w-0.5 h-full bg-slate-200 print:bg-slate-300" style={{ minHeight: '1.5rem' }}></div>
          )}
        </div>

        <Check className="h-5 w-5 text-green-600 mt-0.5" />
        <span className="font-medium text-green-700 pt-0.5 flex-1">
          {planLabel ? planLabel : 'OPERATION COMPLETE'}
        </span>
        <span className="text-sm text-gray-400 font-mono pt-0.5">{time}</span>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // CHECK (Checkbox item) - Phase-aware, Mobile-friendly
  // ═══════════════════════════════════════════════════════════════
  if (entry.type === 'check') {
    const isComplete = entry.completed === true;
    const isSkipped = entry.skipped === true;

    // Phase-aware logic: future phase checks are grayed out and non-interactive
    const isFuturePhase = entry.phase && currentPhase && entry.phase > currentPhase;
    const canClick = isEditable && canModify && !isComplete && !isSkipped && !isFuturePhase;

    // Timeline dot color based on status
    let dotClass = 'border-2 border-cyan-400 bg-white'; // pending (outline only)
    if (isComplete) dotClass = 'bg-green-500';
    else if (isSkipped) dotClass = 'bg-amber-400';

    return (
      <div className="flex items-start gap-3 py-2 px-4">
        {/* Timeline dot column */}
        <div className="relative flex-shrink-0 pt-3">
          <div className={`w-3 h-3 rounded-full ${dotClass}`}></div>
          {/* Vertical connector line */}
          {!isLast && (
            <div className="absolute left-1/2 top-full -translate-x-1/2 w-0.5 h-full bg-slate-200 print:bg-slate-300" style={{ minHeight: '1.5rem' }}></div>
          )}
        </div>

        {/* Check card */}
        <div
          onClick={() => canClick && onCheckComplete?.(entry)}
          role="button"
          tabIndex={canClick ? 0 : -1}
          onKeyDown={(e) => {
            if (canClick && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onCheckComplete?.(entry);
            }
          }}
          className={`
            flex-1 flex items-center gap-3 p-3 rounded-lg border transition-colors text-left
            ${canClick ? 'cursor-pointer' : 'cursor-default'}
            ${isComplete
              ? 'bg-green-50 border-green-200'
              : isSkipped
                ? 'bg-amber-50 border-amber-200'
                : isFuturePhase
                  ? 'bg-slate-50 border-slate-200 opacity-50'
                  : 'bg-white border-slate-200 hover:border-cyan-300 active:bg-cyan-50'
            }
          `}
          style={{ minHeight: '56px' }}
        >
          {/* Time - only show when completed */}
          <span className="text-xs text-slate-400 font-mono w-12 flex-shrink-0">
            {(isComplete || isSkipped) ? (entry.completedAt ? formatTime(entry.completedAt) : time) : ''}
          </span>

          {/* Checkbox circle */}
          <div className={`
            flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all
            ${isComplete
              ? 'bg-green-500 border-green-500'
              : isSkipped
                ? 'bg-amber-400 border-amber-400'
                : isFuturePhase
                  ? 'border-slate-300 bg-slate-100'
                  : 'border-cyan-400 hover:border-cyan-500'
            }
          `}>
            {isComplete && <Check className="h-4 w-4 text-white" />}
            {isSkipped && <X className="h-4 w-4 text-white" />}
          </div>

          {/* Check text */}
          <div className="flex-1 min-w-0">
            <p className={`
              font-medium text-sm
              ${isComplete
                ? 'text-green-700 line-through'
                : isSkipped
                  ? 'text-amber-700 line-through'
                  : isFuturePhase
                    ? 'text-slate-400'
                    : 'text-slate-900'
              }
            `}>
              {entry.text}
            </p>
            {/* Completion info */}
            {isComplete && entry.completedBy && (
              <p className="text-xs text-green-600 mt-0.5">
                {entry.completedBy}
              </p>
            )}
            {/* Skip reason */}
            {isSkipped && entry.skipReason && (
              <p className="text-xs text-amber-600 mt-0.5">
                Skipped: {entry.skipReason}
              </p>
            )}
            {/* Future phase hint */}
            {isFuturePhase && (
              <p className="text-xs text-slate-400 mt-0.5">
                Complete Phase {currentPhase} first
              </p>
            )}
          </div>

          {/* Skip button - only show for actionable checks */}
          {canClick && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCheckSkip?.(entry);
              }}
              className="flex-shrink-0 text-xs text-slate-400 hover:text-amber-600 px-3 py-2 hover:bg-amber-50 rounded-lg transition-colors border border-transparent hover:border-amber-200"
            >
              Skip
            </button>
          )}

          {/* Annotate button - show for completed/skipped checks */}
          {(isComplete || isSkipped) && onAnnotate && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAnnotate(entry);
              }}
              className="flex-shrink-0 p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
              title="Add annotation"
            >
              <MessageSquare className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // POSITION - Card style with blue tint
  // ═══════════════════════════════════════════════════════════════
  if (entry.type === 'position') {
    // Support both 'lon' and 'lng' field names for longitude
    const longitude = entry.lon ?? entry.lng;

    // Pass 2c §1.2 — position entry plan-item-label join.
    // Falls back to generic "Position:" header when the entry pre-dates planItemId
    // stamping, has no planItemId, or its planItemId cannot be resolved in the
    // current voyage's snapshot.
    const planLabel = findLabelByPlanItemId(voyage, entry.planItemId);

    return (
      <div className="flex items-start gap-3 py-2 px-4">
        {/* Timeline dot column */}
        <div className="relative flex-shrink-0 pt-3">
          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
          {/* Vertical connector line */}
          {!isLast && (
            <div className="absolute left-1/2 top-full -translate-x-1/2 w-0.5 h-full bg-slate-200 print:bg-slate-300" style={{ minHeight: '1.5rem' }}></div>
          )}
        </div>

        {/* Card */}
        <div className="flex-1 bg-blue-50 border border-blue-100 rounded-lg p-3" style={{ minHeight: '56px' }}>
          <div className="flex items-center gap-3">
            <MapPin className="h-5 w-5 text-blue-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide mr-2">
                {planLabel ? `${planLabel}:` : 'Position:'}
              </span>
              <span className="text-sm text-slate-700">
                {formatCoordinate(entry.lat, true)}, {formatCoordinate(longitude, false)}
              </span>
              {entry.accuracy && (
                <span className="text-xs text-slate-400 ml-2">(±{entry.accuracy}m)</span>
              )}
              {entry.autoLogged && (
                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium ml-2">
                  Auto
                </span>
              )}
            </div>
            <span className="text-sm text-gray-400 font-mono flex-shrink-0">{time}</span>
          </div>
          {entry.note && (
            <p className="text-xs text-slate-500 mt-2 pl-8">- {entry.note}</p>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // WEATHER - FT-161 Phase 3: Use WeatherReportCard for structured display
  // ═══════════════════════════════════════════════════════════════
  if (entry.type === 'weather') {
    return <WeatherReportCard entry={entry} />;
  }

  // ═══════════════════════════════════════════════════════════════
  // FUEL READING - Card style with amber/red tint
  // ═══════════════════════════════════════════════════════════════
  if (entry.type === 'fuel_reading') {
    const fuelLevel = entry.fuelLevel;
    const isLow = fuelLevel <= 25;

    return (
      <div className="flex items-start gap-3 py-2 px-4">
        {/* Timeline dot column */}
        <div className="relative flex-shrink-0 pt-3">
          <div className={`w-3 h-3 rounded-full ${isLow ? 'bg-red-500' : 'bg-amber-500'}`}></div>
          {/* Vertical connector line */}
          {!isLast && (
            <div className="absolute left-1/2 top-full -translate-x-1/2 w-0.5 h-full bg-slate-200 print:bg-slate-300" style={{ minHeight: '1.5rem' }}></div>
          )}
        </div>

        {/* Card */}
        <div className={`flex-1 rounded-lg p-3 border ${isLow ? 'bg-red-50 border-red-100' : 'bg-amber-50 border-amber-100'}`} style={{ minHeight: '56px' }}>
          <div className="flex items-center gap-3">
            <Fuel className={`h-5 w-5 flex-shrink-0 ${isLow ? 'text-red-500' : 'text-amber-500'}`} />
            <div className="flex-1 min-w-0">
              <span className={`text-xs font-semibold uppercase tracking-wide mr-2 ${isLow ? 'text-red-600' : 'text-amber-600'}`}>Fuel:</span>
              <span className={`text-sm ${isLow ? 'text-red-600 font-medium' : 'text-slate-700'}`}>
                {fuelLevel}%{isLow && ' (LOW)'}
              </span>
            </div>
            <span className="text-sm text-gray-400 font-mono flex-shrink-0">{time}</span>
          </div>
          {entry.notes && (
            <p className="text-xs text-slate-500 mt-2 pl-8">- {entry.notes}</p>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // NOTE - Card style with slate tint
  // ═══════════════════════════════════════════════════════════════
  if (entry.type === 'note') {
    return (
      <div className="flex items-start gap-3 py-2 px-4">
        {/* Timeline dot column */}
        <div className="relative flex-shrink-0 pt-3">
          <div className="w-3 h-3 bg-slate-400 rounded-full"></div>
          {/* Vertical connector line */}
          {!isLast && (
            <div className="absolute left-1/2 top-full -translate-x-1/2 w-0.5 h-full bg-slate-200 print:bg-slate-300" style={{ minHeight: '1.5rem' }}></div>
          )}
        </div>

        {/* Card */}
        <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg p-3" style={{ minHeight: '56px' }}>
          <div className="flex items-start gap-3">
            <FileText className="h-5 w-5 text-slate-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mr-2">Note:</span>
              <span className="text-sm text-slate-700">{entry.text}</span>
              {/* FT-06a: Attribution display */}
              <AttributionBadge createdBy={entry.createdBy} createdAt={entry.createdAt} className="block mt-1" />
            </div>
            <span className="text-sm text-gray-400 font-mono flex-shrink-0">{time}</span>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // COMMS - Card style with purple tint
  // ═══════════════════════════════════════════════════════════════
  if (entry.type === 'comms') {
    return (
      <div className="flex items-start gap-3 py-2 px-4">
        {/* Timeline dot column */}
        <div className="relative flex-shrink-0 pt-3">
          <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
          {/* Vertical connector line */}
          {!isLast && (
            <div className="absolute left-1/2 top-full -translate-x-1/2 w-0.5 h-full bg-slate-200 print:bg-slate-300" style={{ minHeight: '1.5rem' }}></div>
          )}
        </div>

        {/* Card */}
        <div className="flex-1 bg-purple-50 border border-purple-100 rounded-lg p-3" style={{ minHeight: '56px' }}>
          <div className="flex items-center gap-3">
            <Radio className="h-5 w-5 text-purple-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide mr-2">Comms:</span>
              <span className="text-sm text-slate-700">
                {entry.station && <strong>{entry.station}: </strong>}
                {entry.message || entry.text}
              </span>
            </div>
            <span className="text-sm text-gray-400 font-mono flex-shrink-0">{time}</span>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // PHASE MARKER (Multi-phase operation phase divider)
  // ═══════════════════════════════════════════════════════════════
  if (entry.type === 'phase_marker') {
    // Check if this phase is the current active phase
    const isActivePhase = currentPhase === entry.phase;
    const isPastPhase = currentPhase && entry.phase < currentPhase;
    const isFuturePhase = currentPhase && entry.phase > currentPhase;

    return (
      <div className={`
        px-4 py-3 border-l-4 my-1
        ${isActivePhase
          ? 'bg-gradient-to-r from-cyan-50 to-slate-50 border-cyan-500'
          : isPastPhase
            ? 'bg-green-50/50 border-green-400'
            : 'bg-slate-50 border-slate-300'}
      `}>
        <div className="flex items-center gap-2">
          <span className={`
            text-sm font-semibold uppercase tracking-wide flex-1
            ${isActivePhase ? 'text-cyan-700' : isPastPhase ? 'text-green-600' : 'text-slate-500'}
          `}>
            Phase {entry.phase}: {entry.phaseName}
          </span>
          {isPastPhase && (
            <Check className="h-4 w-4 text-green-500" />
          )}
          <span className="text-sm text-gray-400 font-mono">{time}</span>
        </div>
        {entry.phaseStatus && (
          <p className={`text-xs mt-1 ${isFuturePhase ? 'text-slate-400' : 'text-slate-500'}`}>
            {entry.phaseStatus}
          </p>
        )}
      </div>
    );
  }

  // Daily Check completion
  if (entry.type === 'daily_check') {
    return (
      <div className="flex items-start space-x-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
        <ClipboardCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className="font-medium text-emerald-900">
              {entry.category} Daily Checks
            </span>
            <span className="text-sm text-emerald-700 font-medium">
              {entry.passed}/{entry.total} checks complete
            </span>
          </div>
          {entry.completedBy && (
            <p className="text-sm text-emerald-600 mt-1">
              Completed by {entry.completedBy}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // CHECKLIST COMPLETION - FT-82: Operational checklist completed during voyage
  // ═══════════════════════════════════════════════════════════════
  if (entry.type === 'checklist_completion') {
    const passedCount = entry.itemsPassed || 0;
    const totalCount = entry.itemsCompleted || 0;
    const allPassed = passedCount === totalCount && totalCount > 0;

    return (
      <div className="flex items-start gap-3 py-2 px-4">
        {/* Timeline dot column */}
        <div className="relative flex-shrink-0 pt-3">
          <div className={`w-3 h-3 rounded-full ${allPassed ? 'bg-green-500' : 'bg-amber-500'}`}></div>
          {/* Vertical connector line */}
          {!isLast && (
            <div className="absolute left-1/2 top-full -translate-x-1/2 w-0.5 h-full bg-slate-200 print:bg-slate-300" style={{ minHeight: '1.5rem' }}></div>
          )}
        </div>

        {/* Card */}
        <div className={`flex-1 rounded-lg p-3 border ${allPassed ? 'bg-green-50 border-green-100' : 'bg-amber-50 border-amber-100'}`} style={{ minHeight: '56px' }}>
          <div className="flex items-center gap-3">
            <ClipboardCheck className={`h-5 w-5 flex-shrink-0 ${allPassed ? 'text-green-500' : 'text-amber-500'}`} />
            <div className="flex-1 min-w-0">
              <span className={`text-xs font-semibold uppercase tracking-wide mr-2 ${allPassed ? 'text-green-600' : 'text-amber-600'}`}>
                Checklist:
              </span>
              <span className="text-sm text-slate-700 font-medium">
                {entry.checklistName || 'Checklist Completed'}
              </span>
              <span className="text-xs text-slate-500 ml-2">
                ({passedCount}/{totalCount} items)
              </span>
            </div>
            <span className="text-sm text-gray-400 font-mono flex-shrink-0">{time}</span>
          </div>
          {entry.completedBy && (
            <p className="text-xs text-slate-500 mt-2 pl-8">
              Completed by {entry.completedBy}
            </p>
          )}
          {entry.notes && (
            <p className="text-xs text-slate-400 mt-1 pl-8 italic">
              {entry.notes}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // UNKNOWN TYPE (fallback)
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className="flex items-center gap-3 py-2 px-4 text-sm text-slate-400">
      <span className="italic flex-1">Unknown entry type: {entry.type}</span>
      <span className="text-sm text-gray-400 font-mono">{time}</span>
    </div>
  );
};

export default LogEntry;
